import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaMapMarkerAlt,
  FaWifi,
  FaParking,
  FaSwimmingPool,
  FaSmokingBan,
  FaCalendarAlt,
  FaInfoCircle,
  FaCheck,
  FaCreditCard,
  FaLock,
  FaSpinner,
  FaTv,
  FaUtensils,
  FaSnowflake
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { sendBookingNotifications } from '../services/notificationService';
import { getPropertyHostId } from '../utils/hostHelper';
import { getStaffPayloadFromUser, mergeUserWithStaffSession } from '../utils/companyStaffSession';
import { getApiUrl } from '../config/api';
import { isDiscountVisibleToGuest } from '../utils/offerStatus';
import './PaymentPage.css';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { property, checkIn, checkOut, guests, guestData, numberOfNights, pricing, discount, totalPrice, formData } = location.state || {};

  const [paymentMethod, setPaymentMethod] = useState('payAtHotel'); // 'payAtHotel' | 'online'
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [razorpayReady, setRazorpayReady] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.Razorpay) {
      setRazorpayReady(true);
      return;
    }
    const script = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (script) {
      script.addEventListener('load', () => setRazorpayReady(true));
    }
  }, []);

  // Load current user
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setCurrentUser(mergeUserWithStaffSession(parsedUser));
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  }, []);

  // Redirect if no property data
  useEffect(() => {
    if (!property || !checkIn || !checkOut) {
      navigate('/');
    }
  }, [property, checkIn, checkOut, navigate]);

  // Owners are allowed to book their own property — it appears as a normal
  // booking in My Bookings alongside everyone else's bookings.

  // Compute for useMemo (safe when property/checkIn/checkOut missing) - hooks must run before any return
  const checkInDate = checkIn ? new Date(checkIn) : null;
  const checkOutDate = checkOut ? new Date(checkOut) : null;
  const calculatedNumberOfNights = (checkInDate && checkOutDate)
    ? (numberOfNights || Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)))
    : 0;
  const basePrice = property ? (property.pricePerNight || 0) * calculatedNumberOfNights : 0;
  const serviceFeePercent = property?.serviceFeePercent ?? 10;

  const computedDiscountFromProperty = React.useMemo(() => {
    if (!isDiscountVisibleToGuest(property?.discount) || calculatedNumberOfNights === 0) return null;
    const d = property.discount;
    
    // Validate discount dates if provided
    if (checkIn) {
      const checkInDate = new Date(checkIn);
      if (d.validFrom && new Date(d.validFrom) > checkInDate) return null; // Discount not yet valid
      if (d.validUntil && new Date(d.validUntil) < checkInDate) return null; // Discount expired
    }
    
    // Validate minimum stay nights if provided
    if (d.minStayNights && calculatedNumberOfNights < d.minStayNights) return null;
    
    const pricePerNight = property.pricePerNight || 0;
    const originalTotal = pricePerNight * calculatedNumberOfNights;
    let discountedPerNight = pricePerNight;
    let discountText = '';
    
    if (d.type === 'percentage') {
      const perNight = (pricePerNight * d.value) / 100;
      const maxD = d.maxDiscountAmount || Infinity;
      discountedPerNight = pricePerNight - Math.min(perNight, maxD);
      discountText = `${d.value}% OFF`;
    } else if (d.type === 'fixed') {
      discountedPerNight = Math.max(0, pricePerNight - d.value);
      discountText = `₹${d.value} OFF`;
    }
    
    const discountedTotal = discountedPerNight * calculatedNumberOfNights;
    const discountAmount = originalTotal - discountedTotal;
    
    // Only apply discount if discountAmount > 0
    if (discountAmount <= 0) return null;
    
    const serviceFee = Math.round(discountedTotal * (serviceFeePercent / 100));
    const taxes = Math.round(discountedTotal * 0.12);
    const finalTotal = discountedTotal + serviceFee + taxes;
    
    return {
      originalPrice: originalTotal,
      discountedPrice: discountedTotal,
      discountAmount,
      discountText,
      serviceFee,
      taxes,
      finalTotal,
      discount: { ...d, isActive: true, discountText }
    };
  }, [property, calculatedNumberOfNights, serviceFeePercent, checkIn]);

  if (!property || !checkIn || !checkOut) {
    return null;
  }

  // Always use computed discount if property has active discount and pricing doesn't have discountAmount > 0
  const useComputedDiscount = isDiscountVisibleToGuest(property?.discount) && computedDiscountFromProperty && (!pricing || !pricing.discountAmount || pricing.discountAmount === 0);

  // Use passed pricing, or computed discount from property, or fallback
  const fallbackPricing = {
    originalPrice: basePrice,
    discountedPrice: basePrice,
    discountAmount: 0,
    serviceFee: Math.round(basePrice * (serviceFeePercent / 100)),
    taxes: Math.round(basePrice * 0.12),
    finalTotal: basePrice + Math.round(basePrice * (serviceFeePercent / 100)) + Math.round(basePrice * 0.12)
  };
  
  // Priority: computed discount > passed pricing with discount > passed pricing without discount > fallback
  const pricingData = useComputedDiscount
    ? {
        originalPrice: computedDiscountFromProperty.originalPrice,
        discountedPrice: computedDiscountFromProperty.discountedPrice,
        discountAmount: computedDiscountFromProperty.discountAmount,
        serviceFee: computedDiscountFromProperty.serviceFee,
        taxes: computedDiscountFromProperty.taxes,
        finalTotal: computedDiscountFromProperty.finalTotal
      }
    : (pricing && pricing.discountAmount > 0
        ? { ...fallbackPricing, ...pricing, finalTotal: pricing.finalTotal || (pricing.discountedPrice + (pricing.serviceFee || fallbackPricing.serviceFee) + (pricing.taxes || fallbackPricing.taxes)) }
        : (pricing && (pricing.finalTotal > 0 || pricing.discountedPrice != null)
            ? { ...fallbackPricing, ...pricing, finalTotal: pricing.finalTotal || (pricing.discountedPrice + (pricing.serviceFee || fallbackPricing.serviceFee) + (pricing.taxes || fallbackPricing.taxes)) }
            : fallbackPricing));

  const effectiveDiscount = useComputedDiscount ? computedDiscountFromProperty.discount : (isDiscountVisibleToGuest(discount) ? discount : null);
  
  // Cleaning fee: use property's cleaningFee if set, else 2% of room price
  const discountedPrice = pricingData.discountedPrice ?? basePrice;
  const serviceFee = pricingData.serviceFee ?? Math.round(basePrice * (serviceFeePercent / 100));
  const cleaningFee = (property.cleaningFee != null && property.cleaningFee > 0)
    ? property.cleaningFee
    : Math.round((discountedPrice || 0) * 0.02);
  // Use pricingData.finalTotal when we have valid pricing (from booking flow or computed discount)
  const fromBooking = useComputedDiscount || (pricing && pricing.finalTotal > 0 && pricing.discountAmount > 0);
  const computedFinal = (discountedPrice || 0) + (serviceFee || 0) + (cleaningFee || 0);
  // Always use pricingData.finalTotal when available (it includes discount if computed), otherwise computedFinal
  const finalTotal = (useComputedDiscount || (pricing && pricing.finalTotal > 0)) ? pricingData.finalTotal : computedFinal;
  const totalWithDeposit = Math.round(Number(finalTotal)) || Math.round(basePrice * 1.12);
  const displayCleaningFee = fromBooking ? 0 : cleaningFee;

  // Payment schedule
  const paymentDate = new Date(checkIn);
  paymentDate.setDate(paymentDate.getDate() - 3); // 3 days before check-in

  const validateForm = () => {
    const newErrors = {};
    
    if (!consent) {
      newErrors.consent = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const runPayAtHotelFlow = async (bookingPayload, token) => {
    const response = await fetch(getApiUrl('/api/bookings/stay'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ ...bookingPayload, paymentMethod: 'payAtHotel' })
    });
    const result = await response.json();
    if (!result.success) throw new Error(result.message || 'Failed to create booking');
    return result.booking._id;
  };

  const openRazorpayCheckout = (orderData) => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error('Payment gateway not loaded. Please refresh and try again.'));
        return;
      }
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        order_id: orderData.orderId,
        name: 'Book & Stay',
        description: `Booking: ${property?.name || 'Stay'}`,
        handler: function (response) {
          resolve({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
        },
        modal: { ondismiss: () => reject(new Error('Payment cancelled')) }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => reject(new Error(resp.error?.description || 'Payment failed')));
      rzp.open();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Prepare booking data
      const propertyHostId = getPropertyHostId(property);
      console.log('🏠 Resolved property hostId:', propertyHostId);
      console.log('📦 Original property object:', property);
      
      // Verify hostId is set
      if (!propertyHostId) {
        console.error('❌ CRITICAL: No hostId found! Booking will fail!');
        toast.error('Error: Property host information is missing. Please contact support.');
        setLoading(false);
        return;
      }

      // Verify property has a valid MongoDB ObjectId (24 hex chars) - required for booking
      const rawPropertyId = property._id || property.id;
      const isValidObjectId = rawPropertyId && /^[a-fA-F0-9]{24}$/.test(String(rawPropertyId));
      if (!isValidObjectId) {
        console.error('❌ Invalid property ID:', rawPropertyId);
        toast.error('This property cannot be booked directly. Please select a property from our main listings.');
        setLoading(false);
        return;
      }

      // Create booking in database via API
      const token = localStorage.getItem('token');
      const guestCount = (typeof guests === 'object' && guests?.adults != null)
        ? guests.adults
        : (typeof guests === 'number' ? guests : 1);
      const bookingPayload = {
        propertyId: rawPropertyId,
        checkIn: checkIn,
        checkOut: checkOut,
        guests: {
          adults: guestData?.adults ?? (guestCount || 1),
          children: guestData?.children ?? 0,
          pets: guestData?.pets ?? 0
        },
        totalPrice: totalWithDeposit,
        currency: 'INR',
        arrivalTime: formData?.arrivalTime || '',
        specialRequests: formData?.specialRequests || '',
      };

      const staffDetails = getStaffPayloadFromUser(currentUser);
      const companyRole = String(currentUser?.role || '').toLowerCase();
      const needsStaffIdentity = companyRole === 'company_staff' || companyRole === 'company_manager';
      if (needsStaffIdentity && !staffDetails) {
        toast.error('Please log in again and complete staff identity before booking.');
        setLoading(false);
        return;
      }
      if (staffDetails) {
        bookingPayload.staffDetails = staffDetails;
      }

      console.log('� Creating booking via API:', bookingPayload);

      if (paymentMethod === 'online') {
        if (!razorpayReady || !window.Razorpay) {
          toast.error('Payment gateway is loading. Please wait and try again.');
          setLoading(false);
          return;
        }
        const orderRes = await fetch(getApiUrl('/api/payments/create-order'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...bookingPayload, paymentMethod: 'online' })
        });
        const orderResult = await orderRes.json();
        if (!orderResult.success) throw new Error(orderResult.message || 'Could not create payment order');
        const paymentResponse = await openRazorpayCheckout(orderResult);
        const verifyRes = await fetch(getApiUrl('/api/payments/verify'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(paymentResponse)
        });
        const verifyResult = await verifyRes.json();
        if (!verifyResult.success) throw new Error(verifyResult.message || 'Payment verification failed');
        toast.success('Payment Successful');
        const bookingId = verifyResult.booking?._id || orderResult.bookingId;
        const bookingData = { bookingId, property: { ...property, hostId: propertyHostId, hostEmail: property.hostId?.email || property.host?.email, hostName: property.hostId?.name || property.host?.name || 'Property Host' }, checkIn, checkOut, guests, totalPrice: totalWithDeposit, guestInfo: { ...formData, userId: currentUser?._id || currentUser?.id || 'guest123' }, paymentMethod: 'online', status: 'pending' };
        await sendBookingNotifications(bookingData).catch(() => {});
        navigate('/booking-confirmation', { state: { property, checkIn, checkOut, guests, totalPrice: totalWithDeposit, formData, bookingId, paymentMethod: 'online' } });
        return;
      }

      const response = await fetch(getApiUrl('/api/bookings/stay'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...bookingPayload, paymentMethod: 'payAtHotel' })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Failed to create booking');
      const bookingId = result.booking._id;
      const bookingData = { bookingId, property: { ...property, hostId: propertyHostId, hostEmail: property.hostId?.email || property.host?.email, hostName: property.hostId?.name || property.host?.name || 'Property Host' }, checkIn, checkOut, guests, totalPrice: totalWithDeposit, guestInfo: { ...formData, userId: currentUser?._id || currentUser?.id || 'guest123' }, paymentMethod: 'payAtHotel', status: 'pending' };
      await sendBookingNotifications(bookingData).catch(() => {});
      navigate('/booking-confirmation', { state: { property, checkIn, checkOut, guests, totalPrice: totalWithDeposit, formData, bookingId, paymentMethod: 'payAtHotel' } });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to process booking: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getSafeString = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      if (value.city) return String(value.city);
      return fallback;
    }
    return String(value);
  };

  const getAmenityIcon = (amenity) => {
    const a = String(amenity || '').toLowerCase();
    if (a.includes('wifi') || a.includes('internet')) return <FaWifi />;
    if (a.includes('parking')) return <FaParking />;
    if (a.includes('pool') || a.includes('swimming')) return <FaSwimmingPool />;
    if (a.includes('tv') || a.includes('television')) return <FaTv />;
    if (a.includes('kitchen') || a.includes('cooking')) return <FaUtensils />;
    if (a.includes('air') || a.includes('ac') || a.includes('conditioning') || a.includes('heating')) return <FaSnowflake />;
    if (a.includes('smoking') || a.includes('non-smoking')) return <FaSmokingBan />;
    return <FaCheck />;
  };

  return (
    <div className="payment-page">
      <Navbar />
      
      {/* Progress Indicator */}
      <div className="payment-progress">
        <div className="progress-bar-container">
          <div className="progress-step completed">
            <span className="step-number">✓</span>
            <span className="step-label">Your Selection</span>
          </div>
          <div className="progress-line completed"></div>
          <div className="progress-step completed">
            <span className="step-number">✓</span>
            <span className="step-label">Your Details</span>
          </div>
          <div className="progress-line"></div>
          <div className="progress-step active">
            <span className="step-number">3</span>
            <span className="step-label">Finish booking</span>
          </div>
        </div>
      </div>

      <div className="payment-container">
        {/* Left Column - Booking Details */}
        <div className="payment-left-column">
          {/* Property Information */}
          <div className="payment-property-card">
            <h3 className="property-name">{getSafeString(property.name)}</h3>
            <div className="property-location">
              <FaMapMarkerAlt />
              <span>{getSafeString(property.city, 'Unknown Location')}, {getSafeString(property.state, 'India')}</span>
            </div>
            {property.amenities && property.amenities.length > 0 && (
              <div className="property-amenities">
                {property.amenities.slice(0, 4).map((amenity, idx) => (
                  <span key={idx}>{getAmenityIcon(amenity)} {amenity}</span>
                ))}
              </div>
            )}
          </div>

          {/* Booking Details */}
          <div className="payment-booking-details">
            <h3>Your booking details</h3>
            <div className="booking-detail-item">
              <div className="detail-label">Check-in</div>
              <div className="detail-value">
                {checkInDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
              <div className="detail-time">3:00 PM - 11:00 PM</div>
            </div>
            <div className="booking-detail-item">
              <div className="detail-label">Check-out</div>
              <div className="detail-value">
                {checkOutDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
              <div className="detail-time">8:00 AM - 12:00 PM</div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="payment-price-card">
            {/* Discount Badge */}
            {effectiveDiscount && (
              <div className="payment-discount-badge">
                <span className="discount-badge-text">{effectiveDiscount.badge || 'Special Offer'}</span>
                <span className="discount-title">{effectiveDiscount.title}</span>
              </div>
            )}

            <div className="price-total-large">
              <span className="total-label">Your price summary</span>
              <span className="total-amount">₹{totalWithDeposit.toLocaleString()}</span>
            </div>
            
            {/* Savings highlight */}
            {effectiveDiscount && pricingData.discountAmount > 0 && (
              <div className="savings-highlight-payment">
                <span>🎉 You saved ₹{pricingData.discountAmount.toLocaleString()} with this offer!</span>
              </div>
            )}

            <div className="price-note">
              Additional charges may apply
            </div>
            
            <div className="price-breakdown-details">
              <div className="breakdown-section">
                <div className="breakdown-header">
                  <span>Includes ₹{(pricingData.serviceFee + (fromBooking ? (pricingData.taxes || 0) : displayCleaningFee)).toLocaleString()} in fees</span>
                  <a href="#" className="hide-details-link">Hide details</a>
                </div>
                <div className="breakdown-items">
                  <div className="breakdown-item">
                    <span>Room price ({calculatedNumberOfNights} nights):</span>
                    <span>₹{(effectiveDiscount ? pricingData.originalPrice : pricingData.discountedPrice).toLocaleString()}</span>
                  </div>
                  
                  {/* Discount row - offer deducted (e.g. 2000 - 500 = 1500) */}
                  {effectiveDiscount && pricingData.discountAmount > 0 && (
                    <div className="breakdown-item discount-item-payment">
                      <span className="discount-label-payment">
                        <span className="discount-icon-payment">🎯</span>
                        {effectiveDiscount.title} ({effectiveDiscount.discountText})
                      </span>
                      <span className="discount-amount-payment">-₹{pricingData.discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* Subtotal after discount = actual amount before fees */}
                  {effectiveDiscount && pricingData.discountAmount > 0 && (
                    <div className="breakdown-item subtotal-item-payment">
                      <span>Subtotal:</span>
                      <span>₹{pricingData.discountedPrice.toLocaleString()}</span>
                    </div>
                  )}
                  
                  <div className="breakdown-item">
                    <span>Service fee</span>
                    <span>₹{pricingData.serviceFee.toLocaleString()}</span>
                  </div>
                  {fromBooking && (pricingData.taxes || 0) > 0 && (
                    <div className="breakdown-item">
                      <span>Taxes & fees</span>
                      <span>₹{(pricingData.taxes || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {!fromBooking && (
                    <div className="breakdown-item">
                      <span>Cleaning fee</span>
                      <span>₹{displayCleaningFee.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="currency-note">
              <FaInfoCircle />
              <span>All prices shown are in Indian Rupees (₹). You will pay this amount at the property.</span>
            </div>
          </div>

          {/* Cancellation Info */}
          <div className="payment-cancellation">
            <h3>How much will it cost to cancel?</h3>
            <div className="cancellation-item">
              <FaCheck />
              <span>Free cancellation before {new Date(checkInDate.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
            <div className="cancellation-item">
              <span>After 12:00 AM on {new Date(checkInDate.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              <span className="cancellation-amount">₹{Math.round(finalTotal * 0.7).toLocaleString()}</span>
            </div>
          </div>

          {/* Payment Schedule */}
          <div className="payment-schedule">
            <h3>Your payment details</h3>
            {paymentMethod === 'online' ? (
              <>
                <div className="schedule-item highlighted">
                  <div className="schedule-label">Pay now (online)</div>
                  <div className="schedule-amount">₹{totalWithDeposit.toLocaleString()}</div>
                </div>
                <div className="schedule-note pay-at-hotel-note">
                  <FaInfoCircle />
                  <span>You will pay securely via Razorpay (UPI, Card, or QR Code). Your booking is confirmed after successful payment.</span>
                </div>
              </>
            ) : (
              <>
                <div className="schedule-item">
                  <div className="schedule-label">Today you'll pay</div>
                  <div className="schedule-amount">₹0</div>
                </div>
                <div className="schedule-item highlighted">
                  <div className="schedule-label">At the hotel, you'll pay</div>
                  <div className="schedule-amount">₹{totalWithDeposit.toLocaleString()}</div>
                </div>
                <div className="schedule-note pay-at-hotel-note">
                  <FaInfoCircle />
                  <span>You will pay the full amount at the hotel during check-in. Please bring a valid payment method.</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Payment Form */}
        <div className="payment-right-column">
          <form onSubmit={handleSubmit} className="payment-form">
            {/* Payment Method - Pay at Hotel or Online Payment */}
            <div className="form-section">
              <h3>Payment Method</h3>
              <div className="payment-options">
                <div
                  className={`payment-option pay-at-hotel-option ${paymentMethod === 'payAtHotel' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('payAtHotel')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setPaymentMethod('payAtHotel')}
                >
                  <div className="option-icon">
                    <FaCheck />
                  </div>
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-title">Pay at Hotel</span>
                      <span className="recommended-badge">Recommended</span>
                    </div>
                    <div className="option-description">
                      <p>No payment required now. You'll pay the full amount at the hotel during check-in.</p>
                      <ul className="option-benefits">
                        <li><FaCheck /> No online payment needed</li>
                        <li><FaCheck /> Pay with cash or card at hotel</li>
                        <li><FaCheck /> Free cancellation before {new Date(checkInDate.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</li>
                        <li><FaCheck /> Secure and convenient</li>
                      </ul>
                    </div>
                    <div className="accepted-payments">
                      <span className="payment-label">Accepted at hotel:</span>
                      <div className="payment-methods">
                        <span className="payment-badge">💵 Cash</span>
                        <span className="payment-badge">💳 Credit Card</span>
                        <span className="payment-badge">💳 Debit Card</span>
                        <span className="payment-badge">📱 UPI</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`payment-option online-payment-option ${paymentMethod === 'online' ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod('online')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setPaymentMethod('online')}
                >
                  <div className="option-icon">
                    <FaCreditCard />
                  </div>
                  <div className="option-content">
                    <div className="option-header">
                      <span className="option-title">Online Payment</span>
                    </div>
                    <div className="option-description">
                      <p>Pay now securely with UPI, Card, or QR Code via Razorpay.</p>
                      <ul className="option-benefits">
                        <li><FaCheck /> UPI, Debit/Credit Card, Net Banking</li>
                        <li><FaCheck /> QR Code supported</li>
                        <li><FaCheck /> Secure payment (Razorpay)</li>
                        <li><FaCheck /> Instant confirmation</li>
                      </ul>
                    </div>
                    <div className="accepted-payments">
                      <span className="payment-label">Pay now:</span>
                      <div className="payment-methods">
                        <span className="payment-badge">📱 UPI</span>
                        <span className="payment-badge">💳 Card</span>
                        <span className="payment-badge">📷 QR Code</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Information */}
            <div className="form-section important-info-section">
              <h3>Important Information</h3>
              <div className="info-box">
                <div className="info-item">
                  <FaCalendarAlt />
                  <div>
                    <strong>Check-in Requirements</strong>
                    <p>Please bring a valid ID proof and payment method during check-in</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaCreditCard />
                  <div>
                    <strong>Payment Options</strong>
                    <p>Cash, Credit/Debit Cards, and UPI payments accepted at the hotel</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaLock />
                  <div>
                    <strong>Booking Guarantee</strong>
                    <p>Your booking is confirmed. No advance payment required.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Consent */}
            <div className="form-section consent-section">
              <label className="consent-checkbox">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>I consent to receiving additional email marketing communications from book&stay.com which I was not previously subscribed to, including promotions, personalized recommendations, special offers, and other updates and rewards.</span>
              </label>
              {errors.consent && <span className="error-message">{errors.consent}</span>}
            </div>

            {/* Terms */}
            <div className="form-section terms-section">
              <p className="terms-text">
                Your booking is directly with {getSafeString(property.name)} and by completing this booking you agree to the{' '}
                <a href="#">Booking conditions</a>, <a href="#">general terms</a>, <a href="#">privacy policy</a> and <a href="#">Wallet terms</a>.
              </p>
            </div>

            {/* Submit Button */}
            <button type="submit" className="btn-complete-booking" disabled={loading}>
              {loading ? (
                <>
                  <FaSpinner className="spinner" /> Processing...
                </>
              ) : (
                <>
                  <FaCheck /> Confirm Booking
                </>
              )}
            </button>

            <p className="booking-note">
              By clicking "Confirm Booking", you agree to pay at the hotel and accept the booking conditions.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;

